import { useState, useMemo } from 'react';
import { Modal, Input, Select, InputNumber, Switch, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  Pill, Btn, PillTone, avatarSlot,
} from '../../components/ui';
import { toast } from '../../lib/toast';
import { fmtDate } from '../../lib/format';
import { OffboardWizard } from '../../components/OffboardWizard';
import { useAuthStore } from '../../store/auth';
import { useHasPermission } from '../../lib/permissions';

const ROLE_LEVEL: Record<string, number> = { admin: 4, director: 3, manager: 2, sales: 1 };
function canOffboardTarget(actorRole: string, targetRole: string): boolean {
  const a = ROLE_LEVEL[actorRole] ?? 0;
  const t = ROLE_LEVEL[targetRole] ?? 0;
  if (a < 3) return false;        // 仅 admin/director 能操作
  return a > t;                    // 严格大于，同级不能批同级
}

// 追加A⑤：补 academic/academic_director（后端 ROLES 含此两角色，原表缺键 → 角色列显 'academic_director' 英文）
const ROLE_LABEL: Record<string, string> = {
  admin: '管理员', director: '销售总监', manager: '销售主管', sales: '销售员', delivery_lead: '交付主管',
  academic: '教务', academic_director: '教务总监',
};
const ROLE_TONE: Record<string, PillTone> = {
  admin: 'danger', director: 'purple', manager: 'warning', sales: 'primary', delivery_lead: 'default',
  academic: 'primary', academic_director: 'success',
};

type Tab = 'all' | 'active' | 'probation' | 'inactive';

/** 试用期阶梯按规格 3.3 节 */
const PROBATION_LEVELS = [
  { value: 0, label: '不试用' },
  { value: 1, label: '1 级（前 30 天，持客上限 30）' },
  { value: 2, label: '2 级（30-60 天，持客上限 60）' },
  { value: 3, label: '3 级（60-90 天，持客上限 100）' },
];

interface CreateUserForm {
  realName?: string;
  phone?: string;
  email?: string;
  role?: 'admin' | 'director' | 'manager' | 'sales';
  teamId?: number;
  managerId?: number;
  isProbation?: boolean;
  probationLevel?: number;
  customerCapacity?: number;
}

function CreateUserModal({ open, onClose, onSubmitted }: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [form, setForm] = useState<CreateUserForm>({ role: 'sales', customerCapacity: 10000 });
  const [createdCredential, setCreatedCredential] = useState<{ phone: string; password: string } | null>(null);

  // 拉团队列表（选 teamId 用）
  const { data: teams } = useQuery({
    queryKey: ['user-create-teams'],
    queryFn: () => $.get<any[]>('/teams'),
    enabled: open,
  });
  // 拉同团队 manager（选 managerId 用） — 仅在 role=sales 且选了 teamId 时
  const { data: teamManagers } = useQuery({
    queryKey: ['user-create-team-mgrs', form.teamId],
    queryFn: () => $.get<any>('/users', { teamId: form.teamId, pageSize: 100 }),
    enabled: open && form.role === 'sales' && !!form.teamId,
  });
  const eligibleManagers = (teamManagers?.items ?? []).filter(
    (u: any) => u.role === 'manager' || u.role === 'director',
  );

  const create = useMutation({
    mutationFn: (payload: any) => $.post<any>('/users', payload),
    onSuccess: (res: any) => {
      setCreatedCredential({ phone: form.phone!, password: res.initialPassword });
      onSubmitted();
    },
    // 错误 toast 由 axios interceptor 统一展示（避免双弹）
  });

  const reset = () => {
    setForm({ role: 'sales', customerCapacity: 10000 });
    setCreatedCredential(null);
  };

  // 角色变化时清理无关字段
  const onRoleChange = (role: any) => {
    if (role === 'admin' || role === 'director') {
      setForm({ ...form, role, teamId: undefined, managerId: undefined, isProbation: false, customerCapacity: 0 });
    } else {
      // v1.5 F4：sales/manager 默认 10000
      setForm({ ...form, role, customerCapacity: (role === 'sales' || role === 'manager') ? 10000 : 0 });
    }
  };

  const canSubmit = (() => {
    if (!form.realName || !form.phone || !form.role) return false;
    if (!/^1[3-9]\d{9}$/.test(form.phone)) return false;
    // P1-4 拆死锁：sales 只必填团队（上级未填后端按团队主管兜底）；manager 团队选填（先建主管后挂团队）
    if (form.role === 'sales' && !form.teamId) return false;
    return true;
  })();

  // 凭据展示后 — 关闭按钮关闭整个 modal
  if (createdCredential) {
    return (
      <Modal
        open={open}
        title="✓ 用户创建成功"
        onCancel={() => { reset(); onClose(); }}
        footer={[
          <Btn key="close" kind="primary" onClick={() => { reset(); onClose(); }}>关闭</Btn>,
        ]}
        width={480}
      >
        <div style={{ background: 'var(--success-bg)', padding: 16, borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            把以下登录凭据交给该员工。<strong>这是唯一一次展示初始密码</strong>，关闭后无法再查看。
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>登录手机号</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500 }}>{createdCredential.phone}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>初始密码</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500, color: 'var(--primary)' }}>{createdCredential.password}</div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          员工首次登录后将被强制修改密码（must_change_password=1）
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="新建用户"
      onCancel={() => { reset(); onClose(); }}
      onOk={() => create.mutate({
        realName: form.realName,
        phone: form.phone,
        email: form.email,
        role: form.role,
        teamId: form.teamId,
        managerId: form.managerId,
        isProbation: form.isProbation,
        probationLevel: form.probationLevel,
        customerCapacity: form.customerCapacity,
      })}
      okButtonProps={{ disabled: !canSubmit, loading: create.isPending }}
      okText="创建并生成密码"
      cancelText="取消"
      width={520}
    >
      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="form-field">
          <label className="form-label">真实姓名 *</label>
          <Input value={form.realName ?? ''} onChange={(e) => setForm({ ...form, realName: e.target.value })} placeholder="如：张三" />
        </div>
        <div className="form-field">
          <label className="form-label">手机号 *</label>
          <Input value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="11 位手机号" maxLength={11} />
        </div>
        <div className="form-field" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">邮箱（选填）</label>
          <Input value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="可选" />
        </div>
        <div className="form-field" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">角色 *</label>
          <Select
            value={form.role}
            onChange={onRoleChange}
            options={Object.entries(ROLE_LABEL)
              .filter(([k]) => k !== 'delivery_lead')
              .map(([k, v]) => ({ value: k, label: v }))}
            style={{ width: '100%' }}
          />
        </div>
        {(form.role === 'manager' || form.role === 'sales') && (
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">所属团队{form.role === 'sales' ? ' *' : '（选填）'}</label>
            <Select
              placeholder={form.role === 'manager' ? '可留空，先建主管后到团队页挂' : ((teams ?? []).length ? '选择团队' : '请先创建团队')}
              value={form.teamId}
              onChange={(v) => setForm({ ...form, teamId: v, managerId: undefined })}
              options={(teams ?? []).map((t) => ({
                value: Number(t.id),
                label: `${t.name} (主管: ${t.managerName})`,
              }))}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {form.role === 'sales' && form.teamId && (
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">直属上级（选填）</label>
            <Select
              placeholder={eligibleManagers.length ? '同团队主管' : '该团队暂无主管，可留空，提交后自动用团队主管'}
              value={form.managerId}
              onChange={(v) => setForm({ ...form, managerId: v })}
              options={eligibleManagers.map((u: any) => ({
                value: Number(u.id),
                label: `${u.realName} (${u.role === 'manager' ? '主管' : '总监'})`,
              }))}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {form.role === 'sales' && (
          <div className="form-field">
            <label className="form-label">试用期</label>
            <Select
              value={form.probationLevel ?? 0}
              onChange={(v) => setForm({ ...form, probationLevel: v, isProbation: v > 0 })}
              options={PROBATION_LEVELS.map((p) => ({ value: p.value, label: p.label }))}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {/* v1.5 块 1 修：capacity 移出 sales 条件 + max 500→50000，manager/director 也可设上限 */}
        <div className="form-field">
          <label className="form-label">持客容量上限</label>
          <InputNumber
            value={form.customerCapacity}
            onChange={(v) => setForm({ ...form, customerCapacity: Number(v ?? 0) })}
            min={0} max={50000}
            placeholder="0=不接客户 / 上限 50000"
            style={{ width: '100%' }}
          />
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 14 }}>
        username 后端自动生成；初始密码 10 位随机字符，创建后展示一次
      </div>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSubmitted }: {
  user: any | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [form, setForm] = useState<any>({});
  const open = !!user;
  useMemo(() => {
    if (user) setForm({
      realName: user.realName,
      email: user.email ?? '',
      role: user.role,
      teamId: user.teamId ? Number(user.teamId) : undefined,
      managerId: user.managerId ? Number(user.managerId) : undefined,
      customerCapacity: Number(user.customerCapacity ?? 0),
      isActive: !!user.isActive,
    });
  }, [user]);
  const { data: teams } = useQuery({
    queryKey: ['user-edit-teams'],
    queryFn: () => $.get<any[]>('/teams'),
    enabled: open,
  });
  const update = useMutation({
    mutationFn: (payload: any) => $.patch(`/users/${user!.id}`, payload),
    onSuccess: () => {
      toast.success('已更新');
      onSubmitted();
      onClose();
    },
    // 错误 toast 由 axios interceptor 统一展示
  });
  if (!user) return null;
  return (
    <Modal
      open={open}
      title={`编辑用户 · ${user.realName}`}
      onCancel={onClose}
      onOk={() => update.mutate(form)}
      okButtonProps={{ loading: update.isPending }}
      okText="保存"
      cancelText="取消"
      width={480}
    >
      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="form-field">
          <label className="form-label">姓名</label>
          <Input value={form.realName ?? ''} onChange={(e) => setForm({ ...form, realName: e.target.value })} />
        </div>
        <div className="form-field">
          <label className="form-label">手机号</label>
          <Input value={user.phone} disabled />
        </div>
        <div className="form-field" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">角色</label>
          <Select
            value={form.role}
            onChange={(v) => setForm({ ...form, role: v })}
            options={Object.entries(ROLE_LABEL)
              .filter(([k]) => k !== 'delivery_lead')
              .map(([k, v]) => ({ value: k, label: v }))}
            style={{ width: '100%' }}
          />
        </div>
        {(form.role === 'manager' || form.role === 'sales') && (
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">所属团队</label>
            <Select
              value={form.teamId}
              /* P1-4 评审 major：改团队同时清旧上级 managerId，避免带陈旧上级触发"团队不一致"400；后端按新团队主管兜底 */
              onChange={(v) => setForm({ ...form, teamId: v, managerId: undefined })}
              options={(teams ?? []).map((t) => ({ value: Number(t.id), label: t.name }))}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div className="form-field">
          <label className="form-label">持客容量</label>
          {/* v1.5 块 1 修：max 500→50000 + 加 placeholder */}
          <InputNumber value={form.customerCapacity}
                       onChange={(v) => setForm({ ...form, customerCapacity: Number(v ?? 0) })}
                       min={0} max={50000}
                       placeholder="0=不接客户 / 上限 50000"
                       style={{ width: '100%' }} />
        </div>
        <div className="form-field">
          <label className="form-label">状态</label>
          <Switch checked={form.isActive} onChange={(v) => setForm({ ...form, isActive: v })} checkedChildren="在岗" unCheckedChildren="停用" />
        </div>
      </div>
    </Modal>
  );
}

/** 重置密码后显示新密码的 modal */
function ResetPasswordModal({ result, onClose }: { result: { phone: string; password: string } | null; onClose: () => void }) {
  if (!result) return null;
  return (
    <Modal
      open={!!result}
      title="✓ 密码已重置"
      onCancel={onClose}
      footer={[<Btn key="close" kind="primary" onClick={onClose}>关闭</Btn>]}
      width={440}
    >
      <div style={{ background: 'var(--warning-bg)', padding: 16, borderRadius: 8 }}>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          新密码已生成。<strong>务必现在交给用户</strong>，关闭后无法再查看。
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>手机号</div>
            <div style={{ fontFamily: 'monospace', fontSize: 16 }}>{result.phone}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>新密码</div>
            <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500, color: 'var(--primary)' }}>{result.password}</div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function AdminUsersPage() {
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user)!;
  // 大权限批：页内操作按钮逐个按权限点门控（前端 UX 层；后端 @RequirePermissions + team-scope 才是安全边界）。
  const canCreate = useHasPermission('user:create');
  const canEdit = useHasPermission('user:edit');
  const canDeactivate = useHasPermission('user:deactivate'); // 停用/启用同权限点（避免能停不能启）
  const canResetPwd = useHasPermission('user:reset_password');
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [resetResult, setResetResult] = useState<{ phone: string; password: string } | null>(null);
  const [offboarding, setOffboarding] = useState<any | null>(null);
  const pageSize = 20;

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (tab === 'probation') p.isProbation = 1;
    if (tab === 'inactive') p.isActive = 0;
    if (tab === 'active') p.isActive = 1;
    return p;
  }, [page, pageSize, tab]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', params],
    queryFn: () => $.get<{ items: any[]; total: number }>('/users', params),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => $.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success('已停用用户');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    // ux1：Modal.confirm onOk 改 mutateAsync 后失败会 reject，补 onError toast 避免静默
    onError: (e: any) => toast.error(e?.message ?? '停用失败'),
  });

  const update = useMutation({
    mutationFn: ({ id, dto }: any) => $.patch(`/users/${id}`, dto),
    onSuccess: () => {
      toast.success('已更新');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const resetPwd = useMutation({
    mutationFn: ({ id, phone }: { id: number; phone: string }) =>
      $.post<any>(`/users/${id}/reset-password`).then((res) => ({ ...res, phone })),
    onSuccess: (res: any) => {
      setResetResult({ phone: res.phone, password: res.initialPassword });
    },
    // ux1：Modal.confirm onOk 改 mutateAsync 后失败会 reject，补 onError toast 避免静默
    onError: (e: any) => toast.error(e?.message ?? '重置失败'),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeCount = items.filter((u) => u.isActive).length;
  const probationCount = items.filter((u) => u.isProbation).length;
  const inactiveCount = items.filter((u) => !u.isActive).length;

  return (
    <>
      <PageHeader
        breadcrumb={['系统', '用户管理']}
        title="用户管理"
        subtitle="所有系统账号 · 角色 · 持客容量"
        actions={canCreate ? <Btn kind="primary" onClick={() => setCreateOpen(true)}>+ 新建用户</Btn> : null}
      />
      <div className="work-content">
        <StatsRow>
          <StatCard label="账号总数" value={total} unit="人" />
          <StatCard label="在岗" value={activeCount} unit="人" valueColor="success" />
          <StatCard label="试用期" value={probationCount} unit="人" valueColor="warning" />
          <StatCard label="已停用" value={inactiveCount} unit="人" />
        </StatsRow>

        <FilterBar
          active={tab}
          onChange={(k) => { setTab(k as Tab); setPage(1); }}
          tabs={[
            { key: 'all', label: '全部', count: total },
            { key: 'active', label: '在岗', count: activeCount },
            { key: 'probation', label: '试用期', count: probationCount },
            { key: 'inactive', label: '已停用', count: inactiveCount },
          ]}
        />
        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                <th style={{ width: '22%' }}>账号</th>
                <th style={{ width: '10%' }}>角色</th>
                <th style={{ width: '12%' }}>手机号</th>
                <th style={{ width: '16%' }}>持客容量</th>
                <th style={{ width: '10%' }}>状态</th>
                <th style={{ width: '11%' }}>最近登录</th>
                <th style={{ width: '19%', textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无用户</td></tr>
              )}
              {items.map((u: any) => {
                const slot = avatarSlot(u.id);
                const cap = Number(u.customerCapacity ?? 0);
                const cur = Number(u.currentCustomerCount ?? 0);
                const pct = cap > 0 ? Math.round((cur / cap) * 100) : 0;
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="cust-cell">
                        {u.avatarUrl ? (
                          <img src={u.avatarUrl} alt={u.realName} className="cust-avatar"
                               style={{ objectFit: 'cover' }} />
                        ) : (
                          <div className={`cust-avatar c${slot}`}>{(u.realName ?? u.username ?? '?').charAt(0)}</div>
                        )}
                        <div className="cust-info">
                          <div className="cust-name">{u.realName ?? u.username}</div>
                          <div className="cust-meta">@{u.username} · ID {u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td><Pill tone={ROLE_TONE[u.role] ?? 'default'}>{ROLE_LABEL[u.role] ?? u.role}</Pill></td>
                    <td><span className="time-text">{u.phone ?? '—'}</span></td>
                    <td>
                      {cap === 0
                        ? <span className="time-text muted">— 不持客</span>
                        : (
                          <div className="tbl-progress">
                            <div className="tbl-progress-bar">
                              <div className="tbl-progress-fill" style={{
                                width: `${Math.min(100, pct)}%`,
                                background: pct >= 80 ? 'var(--warning)' : 'var(--success)',
                              }} />
                            </div>
                            <span className="tbl-progress-val">{cur}/{cap}</span>
                          </div>
                        )}
                    </td>
                    <td>
                      {!u.isActive ? <Pill tone="default">已停用</Pill>
                        : u.isProbation ? <Pill tone="warning">试用期</Pill>
                        : <Pill tone="success">在岗</Pill>}
                    </td>
                    <td>
                      <span className="time-text muted">
                        {u.lastLoginAt ? fmtDate(u.lastLoginAt) : '从未登录'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        {canEdit && <button className="row-action" onClick={() => setEditing(u)} data-tip="编辑账号">编辑</button>}
                        {canResetPwd && <button
                          className="row-action"
                          onClick={() => {
                            Modal.confirm({
                              title: `重置 ${u.realName} 的密码？`,
                              content: '会生成 10 位随机新密码，仅展示一次。原密码立即失效。',
                              okText: '确认重置',
                              okType: 'primary',
                              cancelText: '取消',
                              onOk: () => resetPwd.mutateAsync({ id: u.id, phone: u.phone }),
                            });
                          }}
                          data-tip="生成新密码"
                        >重置密码</button>}
                        {canDeactivate && (u.isActive ? (
                          <>
                            {(() => {
                              const allow = canOffboardTarget(actor.role, u.role);
                              return (
                                <button
                                  className={`row-action danger ${allow ? '' : 'disabled'}`}
                                  onClick={() => allow && setOffboarding(u)}
                                  disabled={!allow}
                                  data-tip={allow
                                    ? '离职继承（推荐用此走转移流程）'
                                    : `无权限：${actor.role} 不能离职 ${u.role}（仅能批级别更低的员工）`}
                                  style={!allow ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                                >离职</button>
                              );
                            })()}
                            <button
                              className="row-action danger"
                              onClick={() => {
                                Modal.confirm({
                                  title: `确认停用 ${u.realName ?? u.username}？`,
                                  content: '停用后该账号无法登录。其持有的客户保留归属，需要时再单独转移。如需转客户，请用"离职"按钮。',
                                  okText: '确认停用',
                                  okType: 'danger',
                                  cancelText: '取消',
                                  onOk: () => deactivate.mutateAsync(u.id),
                                });
                              }}
                              data-tip="停用此账号"
                            >停用</button>
                          </>
                        ) : (
                          <button
                            className="row-action"
                            onClick={() => update.mutate({ id: u.id, dto: { isActive: true } })}
                            data-tip="恢复账号"
                          >启用</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 0 && <TableFooter total={total} page={page} pageSize={pageSize} onPage={setPage} />}
        </TableWrap>
      </div>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />
      <EditUserModal
        user={editing}
        onClose={() => setEditing(null)}
        onSubmitted={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />
      <ResetPasswordModal result={resetResult} onClose={() => setResetResult(null)} />
      <OffboardWizard
        open={!!offboarding}
        user={offboarding}
        onClose={() => setOffboarding(null)}
        onDone={() => {
          setOffboarding(null);
          qc.invalidateQueries({ queryKey: ['admin-users'] });
        }}
      />
    </>
  );
}
